"use client";

import { LDProvider, useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import type { LDClient } from "launchdarkly-js-client-sdk";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAppSelector } from "@/redux/store";

import { registerLaunchDarklyClient } from "./ldClientBridge";
import { FLAGS, type FlagId } from "./registry";

type FlagKey = (typeof FLAGS)[FlagId]["key"];

export type FlagsContextValue = Partial<Record<FlagKey, boolean | string>>;

const FlagsContext = createContext<FlagsContextValue>({});

function parseEnvOverrides(): FlagsContextValue {
  if (typeof process === "undefined" || !process.env.NEXT_PUBLIC_EW_FLAGS) {
    return {};
  }
  try {
    return JSON.parse(process.env.NEXT_PUBLIC_EW_FLAGS) as FlagsContextValue;
  } catch {
    return {};
  }
}

function buildDefaults(): FlagsContextValue {
  const out: FlagsContextValue = {};
  (Object.keys(FLAGS) as FlagId[]).forEach((id) => {
    const def = FLAGS[id];
    out[def.key] = def.defaultValue;
  });
  return out;
}

function coerceFlagValue(raw: unknown, fallback: boolean | string): boolean | string {
  if (typeof raw === "boolean" || typeof raw === "string") {
    return raw;
  }
  return fallback;
}

function mergeFlagsFromLd(
  defaults: FlagsContextValue,
  ldFlags: Record<string, unknown>,
  env: FlagsContextValue,
): FlagsContextValue {
  const out: FlagsContextValue = { ...defaults };
  (Object.keys(FLAGS) as FlagId[]).forEach((id) => {
    const def = FLAGS[id];
    const key = def.key;
    if (Object.prototype.hasOwnProperty.call(env, key)) {
      out[key] = coerceFlagValue(env[key], def.defaultValue);
    } else if (Object.prototype.hasOwnProperty.call(ldFlags, key)) {
      out[key] = coerceFlagValue(ldFlags[key], def.defaultValue);
    }
  });
  return out;
}

function FlagsStaticProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => {
    return { ...buildDefaults(), ...parseEnvOverrides() };
  }, []);
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

function FlagsLdSync({ children }: { children: ReactNode }) {
  const ldFlags = useFlags() as Record<string, unknown>;
  const ldClient = useLDClient();
  const groups = useAppSelector((s) => s.groupManager.groups) as { name?: string };
  const groupName = groups?.name;

  const envOverrides = useMemo(() => parseEnvOverrides(), []);

  useEffect(() => {
    if (!ldClient) {
      registerLaunchDarklyClient(null);
      return;
    }

    const ctx = {
      kind: "user" as const,
      key: `group-${(groupName || "unknown").toLowerCase()}`,
      groups: groupName ? [groupName] : [],
    };

    let cancelled = false;
    void (async () => {
      try {
        await ldClient.identify(ctx);
        if (!cancelled) {
          registerLaunchDarklyClient(ldClient as LDClient);
        }
      } catch {
        if (!cancelled) {
          registerLaunchDarklyClient(ldClient as LDClient);
        }
      }
    })();

    return () => {
      cancelled = true;
      registerLaunchDarklyClient(null);
    };
  }, [ldClient, groupName]);

  const value = useMemo(() => {
    const defaults = buildDefaults();
    return mergeFlagsFromLd(defaults, ldFlags, envOverrides);
  }, [ldFlags, envOverrides]);

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

type ReactConfig = { LAUNCHDARKLY_CLIENT_ID?: string };

const BUILD_TIME_LD_CLIENT_ID = (
  process.env.NEXT_PUBLIC_LD_CLIENT_ID ??
  process.env.REACT_APP_LD_CLIENT_ID ??
  ""
).trim();

function readRuntimeLdClientSideId(): string {
  if (typeof window === "undefined") return "";
  const runtime = (window as unknown as { __REACT_CONFIG__?: ReactConfig }).__REACT_CONFIG__
    ?.LAUNCHDARKLY_CLIENT_ID;
  return (runtime ?? "").trim();
}

export function FlagsProvider({ children }: { children: ReactNode }) {
  const [ldClientSideId, setLdClientSideId] = useState<string>(BUILD_TIME_LD_CLIENT_ID);

  useEffect(() => {
    const runtimeId = readRuntimeLdClientSideId();
    if (runtimeId && runtimeId !== ldClientSideId) {
      setLdClientSideId(runtimeId);
    }
    if (process.env.NODE_ENV !== "production" && !runtimeId && !BUILD_TIME_LD_CLIENT_ID) {
      // eslint-disable-next-line no-console
      console.warn(
        "[flags] No LaunchDarkly client-side ID found (window.__REACT_CONFIG__.LAUNCHDARKLY_CLIENT_ID / NEXT_PUBLIC_LD_CLIENT_ID / REACT_APP_LD_CLIENT_ID). " +
          "All flags will resolve to their registry defaults.",
      );
    }
  }, [ldClientSideId]);

  if (!ldClientSideId) {
    return <FlagsStaticProvider>{children}</FlagsStaticProvider>;
  }

  return (
    <LDProvider
      clientSideID={ldClientSideId}
      reactOptions={{ useCamelCaseFlagKeys: true }}
    >
      <FlagsLdSync>{children}</FlagsLdSync>
    </LDProvider>
  );
}

export function useFlagsContext() {
  return useContext(FlagsContext);
}
